import React, { useState, useContext } from 'react';
import { graphql } from 'gatsby';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { defaultDomainCountryCodes } from '../../global/countries';
//@ts-ignore
import * as styles from './product-family-carousel.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import ArrowLgButton from '../Global/ArrowLgButton';
import CtaLink from '../Global/CtaLink';
import { ProductPagePreview } from '../Global/DataUtils/productQueries';
import {
    ProductDescription,
    ProductDetailMastheadData,
    ProductSpecifications,
} from '../../templates/StructuredDataContext';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    getIcon,
    getMediaData,
    getMultiChoice,
    MediaData,
} from '../Global/DataUtils';
import { ProductFamilyCarouselProps } from '.';

type ProductFamilyCarouselItem = {
    product: ProductPagePreview;
    specifications: ProductSpecifications;
    description: ProductDescription;
    masthead: ProductDetailMastheadData;
    largeImage?: MediaData | null;
    smallImage?: MediaData | null;
};
interface Props extends ProductFamilyCarouselProps {
    currentPageSlug?: string;
}

const ProductFamilyCarousel: React.FC<Props> = (props) => {
    const [currentProduct, setCurrentProduct] = useState(0);
    const breakpoints = useBreakpoint();
    const selectProducts = (): ProductFamilyCarouselItem[] => {
        let selectedProducts: ProductFamilyCarouselItem[] = [];

        const getProductFamilyData = (product: ProductPagePreview) => {
            const specifications =
                product.elements?.page_components?.value?.filter(
                    (component) =>
                        component.__typename ===
                        'kontent_item_component___product_specifications'
                )[0] as ProductSpecifications;

            const largeImage = getMediaData(
                specifications?.elements?.specs?.value[0]?.elements
                    .product_family_image_large
            );

            const smallImage = getMediaData(
                specifications?.elements?.specs?.value[0]?.elements
                    .product_family_image_small
            );

            const productDescription =
                product.elements.page_components.value.filter(
                    (component) =>
                        component.__typename ===
                        'kontent_item_component___product_description'
                )[0] as ProductDescription;

            const productDetailMasthead =
                product.elements.page_components.value.filter(
                    (component) =>
                        component.__typename ===
                        'kontent_item_component___product_detail_masthead'
                )[0] as ProductDetailMastheadData;

            return {
                product,
                specifications: specifications,
                description: productDescription,
                masthead: productDetailMasthead,
                largeImage,
                smallImage,
            };
        };

        if (
            getMultiChoice(props.configuration_option) ===
            'product_family_data_set'
        ) {
            props.product_family?.value[0]?.elements.products?.value.map(
                (product) => {
                    const productFamilyData = getProductFamilyData(product);

                    if (
                        productFamilyData.product.elements?.slug?.value !==
                            props.currentPageSlug &&
                        productFamilyData.largeImage?.url
                    ) {
                        selectedProducts.push(productFamilyData);
                    }
                }
            );
        } else {
            props.explicit_products?.value.map((product) => {
                const productFamilyData = getProductFamilyData(product);

                if (
                    productFamilyData.product.elements?.slug?.value !==
                        props.currentPageSlug &&
                    productFamilyData.largeImage?.url
                ) {
                    selectedProducts.push(productFamilyData);
                }
            });
        }

        return selectedProducts;
    };

    const productPages = selectProducts();

    const numProducts = productPages?.length || 0;

    const handleClickPrev = () => {
        if (numProducts && currentProduct === 0) {
            return setCurrentProduct(numProducts - 1);
        }
        return setCurrentProduct(currentProduct - 1);
    };

    const handleClickNext = () => {
        if (numProducts && currentProduct === numProducts - 1) {
            return setCurrentProduct(0);
        }
        return setCurrentProduct(currentProduct + 1);
    };

    const renderPrevButton = () => (
        <div className={styles.arrowButtonContainer}>
            <ArrowLgButton
                strokeColor="#FFFFFF"
                direction="left"
                handleClick={handleClickPrev}
                className={styles.prev}
            />
        </div>
    );

    const renderNextButton = () => (
        <div className={styles.arrowButtonContainer}>
            <ArrowLgButton
                strokeColor="#FFFFFF"
                direction="right"
                handleClick={handleClickNext}
                className={styles.next}
            />
        </div>
    );

    const renderCta = (productDescription: ProductDescription) => {
        if (!productDescription) return null;

        const ctaTarget =
            productDescription?.elements?.cta?.cta_configuration__cta_window_action?.value.toString() ===
            'New Window'
                ? '_new'
                : '_same';

        const ctaLocation =
            productDescription?.elements?.cta?.cta_configuration__cta_location;

        const ctaHref = BuildLinkHref(ctaLocation);

        const ctaLabel =
            productDescription?.elements?.cta?.cta_configuration__cta_label;

        return (
            <div className={styles.cta}>
                {ctaHref && (
                    <CtaLink href={ctaHref} target={ctaTarget}>
                        {ctaLabel?.value}
                    </CtaLink>
                )}{' '}
            </div>
        );
    };

    const renderProduct = (product: ProductFamilyCarouselItem, idx: number) => {
        if (idx === currentProduct) {
            const specifications = product.specifications;

            const specsInfo =
                specifications.elements?.specs?.value[0]?.elements
                    .product_family_carousel_info?.value[0]?.elements;

            const largeImage = product.largeImage;

            const smallImage = product.smallImage;

            const capacity =
                specifications?.elements?.specs?.value[0]?.elements?.capacity
                    ?.value[0]?.elements;

            const productDescription = product.description;
            let productUrl = product?.product?.elements?.slug?.url;
            const pageMetadata = useContext(PageMetadataContext);

            const preferredLanguage =
                pageMetadata.preferredLanguage || 'default';
            productUrl =
                defaultDomainCountryCodes.indexOf(
                    preferredLanguage.toLowerCase()
                ) > -1
                    ? productUrl
                    : `/${preferredLanguage.toLowerCase()}${productUrl}`;

            const productDetailMasthead = product.masthead;

            const ctaLocation =
                productDescription?.elements?.cta
                    ?.cta_configuration__cta_location;

            const ctaHref = BuildLinkHref(ctaLocation);

            const capacityIcon = getIcon(capacity.icon);

            return (
                <React.Fragment key={idx}>
                    {largeImage && (
                        <div className={styles.productImageContainer}>
                            <picture>
                                {smallImage?.url && (
                                    <source
                                        media="(max-width: 576px)"
                                        srcSet={smallImage?.url}
                                    />
                                )}

                                <img
                                    src={largeImage?.url}
                                    alt={largeImage?.description}
                                    className={styles.productImage}
                                />
                            </picture>
                        </div>
                    )}
                    <div className={styles.contentContainer}>
                        <div className={styles.title}>
                            {props.display_title.value}
                        </div>
                        <div className={styles.productCarousel}>
                            {currentProduct !== 0 && breakpoints.xl
                                ? renderPrevButton()
                                : null}
                            {currentProduct !== numProducts - 1 &&
                            breakpoints.xl
                                ? renderNextButton()
                                : null}

                            <div key={idx} className={styles.product}>
                                <div className={styles.productDescription}>
                                    <div className={styles.productName}>
                                        <div>
                                            <a href={productUrl}>
                                                <strong>
                                                    {
                                                        productDetailMasthead
                                                            ?.elements
                                                            .product_name_for_display
                                                            ?.value
                                                    }
                                                </strong>
                                            </a>
                                        </div>
                                        {currentProduct !== 0 && !breakpoints.xl
                                            ? renderPrevButton()
                                            : null}
                                        {currentProduct !== numProducts - 1 &&
                                        !breakpoints.xl
                                            ? renderNextButton()
                                            : null}
                                    </div>
                                    {productDescription?.elements
                                        ?.short_description?.value && (
                                        <p className={styles.summary}>
                                            {
                                                productDescription?.elements
                                                    ?.short_description?.value
                                            }
                                        </p>
                                    )}

                                    {breakpoints.xl && ctaHref
                                        ? renderCta(productDescription)
                                        : null}
                                </div>
                                {specifications && (
                                    <div className={styles.specifications}>
                                        {specsInfo.value_1 && (
                                            <div
                                                className={styles.specification}
                                            >
                                                <p
                                                    className={
                                                        styles.subheading
                                                    }
                                                >
                                                    Dimensions
                                                </p>
                                                <ul>
                                                    <li>
                                                        {
                                                            specsInfo.label_1
                                                                .value
                                                        }
                                                        {
                                                            specsInfo.value_1
                                                                .value
                                                        }
                                                    </li>
                                                    <li>
                                                        {
                                                            specsInfo.label_2
                                                                .value
                                                        }
                                                        {
                                                            specsInfo.value_2
                                                                .value
                                                        }
                                                    </li>
                                                    <li>
                                                        {
                                                            specsInfo.label_3
                                                                .value
                                                        }
                                                        {
                                                            specsInfo.value_3
                                                                .value
                                                        }
                                                    </li>
                                                </ul>
                                            </div>
                                        )}

                                        {specifications.elements.specs
                                            .value[0] && (
                                            <div
                                                className={styles.specification}
                                            >
                                                <p
                                                    className={
                                                        styles.subheading
                                                    }
                                                >
                                                    Capacity
                                                </p>
                                                <ul>
                                                    {capacityIcon?.url && (
                                                        <li>
                                                            <object
                                                                type="image/svg+xml"
                                                                data={
                                                                    capacityIcon?.url
                                                                }
                                                            >
                                                                <img
                                                                    src={
                                                                        capacityIcon?.url
                                                                    }
                                                                    alt={capacity.capacity_value?.value?.toString()}
                                                                />
                                                            </object>
                                                        </li>
                                                    )}
                                                    <li
                                                        className={
                                                            styles.capacity
                                                        }
                                                    >
                                                        {capacity.capacity_value?.value?.toString()}
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!breakpoints.xl && ctaHref
                                    ? renderCta(productDescription)
                                    : null}
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            );
        }
    };

    return (
        <div className={styles.wrapper} id={props.anchor_name?.value}>
            {productPages?.map((product, idx) => {
                return renderProduct(product, idx);
            })}
        </div>
    );
};

export default ProductFamilyCarousel;
